/** @jsxImportSource @emotion/react */
import Pagination from 'components/atoms/Pagination'
import { Popover } from 'components/atoms/Popover'
import Toast from 'components/atoms/Toast'
import { StatePill } from 'components/lab/atoms/StatePill'
import { PrintRunDetailsModal } from 'components/lab/billingPatientStatements/PrintRunDetailsModal'
import { apiErrorMessage, formatDateTime } from 'components/lab/billingPatientStatements/statementFormat'
import { confirm } from 'components/molecules/Confirmation'
import React from 'react'
import {
  useCancelStatementPrintRunMutation,
  useGetStatementPrintRunsQuery,
  useLazyGetStatementPrintRunDownloadQuery,
} from 'store/services/paperStatements'
import { StatementPrintRunDto } from 'store/types'

type Props = {
  onConfirmRun: (runId: string) => void
}

export const PrintRunsListing: React.FC<Props> = ({ onConfirmRun }) => {
  const [page, setPage] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(20)
  const [detailsRunId, setDetailsRunId] = React.useState<string | null>(null)
  const [activeRowPopover, setActiveRowPopover] = React.useState<string | null>(null)
  const { data, isFetching } = useGetStatementPrintRunsQuery({ page, size: pageSize })
  const [getRunDownload] = useLazyGetStatementPrintRunDownloadQuery()
  const [cancelRun] = useCancelStatementPrintRunMutation()

  const handleDownload = async (run: StatementPrintRunDto) => {
    try {
      const link = await getRunDownload(run.id).unwrap()
      window.open(link.url, '_blank')?.focus()
    } catch (error) {
      Toast({ type: 'error', label: apiErrorMessage(error, 'Unable to download the printed statements') })
    }
  }

  const handleCancel = async (run: StatementPrintRunDto) => {
    try {
      await confirm('', {
        title: 'Cancel print run?',
        description: `All ${run.totalCount} statement(s) from this run go back to Ready to Print.`,
        yesAction: 'Cancel run',
        noAction: 'Keep',
        yesColor: 'danger',
      })
    } catch {
      return
    }

    try {
      await cancelRun(run.id).unwrap()
      Toast({ type: 'success', label: 'Print run cancelled' })
    } catch (error) {
      Toast({ type: 'error', label: apiErrorMessage(error, 'Unable to cancel the print run') })
    }
  }

  const renderStatus = (run: StatementPrintRunDto) => {
    if (run.cancelledAt) {
      return (
        <div>
          <StatePill color="#6c757d" label="Cancelled" />
          <div className="textGray" css={{ fontSize: 12 }}>
            {formatDateTime(run.cancelledAt)} by {run.cancelledByName || '--'}
          </div>
        </div>
      )
    }

    if (run.confirmedAt) {
      return (
        <div>
          <StatePill color="#28a745" label="Confirmed" />
          <div className="textGray" css={{ fontSize: 12 }}>
            {formatDateTime(run.confirmedAt)} by {run.confirmedByName || '--'}
          </div>
        </div>
      )
    }

    return <StatePill color="#fd7e14" label="Awaiting confirmation" />
  }

  const isOpen = (run: StatementPrintRunDto) => !run.confirmedAt && !run.cancelledAt

  const runAction = (action: () => void) => {
    setActiveRowPopover(null)
    action()
  }

  return (
    <div>
      <div className="table-responsive" css={{ width: '100%', overflow: 'visible' }}>
        <table className="table table-sm" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Printed</th>
              <th>Printed By</th>
              <th>Status</th>
              <th>Statements</th>
              <th>Mailed</th>
              <th>Not Mailed</th>
              <th>Returned</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {isFetching && (
              <tr>
                <td colSpan={8} className="text-center">Loading...</td>
              </tr>
            )}
            {!isFetching && !data?.items?.length && (
              <tr>
                <td colSpan={8} className="text-center">No print runs yet</td>
              </tr>
            )}
            {!isFetching && data?.items?.map((run: StatementPrintRunDto) => (
              <tr key={run.id}>
                <td>{formatDateTime(run.printedAt)}</td>
                <td>{run.printedByName || '--'}</td>
                <td>{renderStatus(run)}</td>
                <td>{run.totalCount}</td>
                <td>{run.mailedCount}</td>
                <td>{run.notMailedCount}</td>
                <td>{run.returnedCount}</td>
                <td style={{ position: 'relative' }}>
                  <button
                    className="btn btn-link p-0"
                    onClick={() => setActiveRowPopover(activeRowPopover === run.id ? null : run.id)}>
                    <i className="fas fa-ellipsis-h" />
                  </button>
                  <Popover
                    open={activeRowPopover === run.id}
                    setOpen={(open) => setActiveRowPopover(open ? run.id : null)}>
                    <div css={{ display: 'flex', flexDirection: 'column' }}>
                      {isOpen(run) && (
                        <button className="btn btn-link text-left" onClick={() => runAction(() => onConfirmRun(run.id))}>
                          Confirm mailed
                        </button>
                      )}
                      <button className="btn btn-link text-left" onClick={() => runAction(() => setDetailsRunId(run.id))}>
                        Details
                      </button>
                      <button className="btn btn-link text-left" onClick={() => runAction(() => handleDownload(run))}>
                        Download PDF
                      </button>
                      {isOpen(run) && (
                        <button
                          className="btn btn-link text-left text-danger"
                          onClick={() => runAction(() => handleCancel(run))}>
                          Cancel run
                        </button>
                      )}
                    </div>
                  </Popover>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        onPageChange={setPage}
        totalCount={data?.totalItems ?? 0}
        siblingCount={1}
        currentPage={page}
        pageSize={pageSize}
        className=""
        setPageSize={(size: number) => {
          setPage(0)
          setPageSize(size)
        }}
      />
      <PrintRunDetailsModal runId={detailsRunId} onHide={() => setDetailsRunId(null)} />
    </div>
  )
}
